// # FAQ page component
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardHeader from '../../components/DashboardHeader';
import Header from '../components/header';  // # Import regular header
import DashboardFooter from '../../components/DashboardFooter';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../auth/firebase';
import { useAuthStore } from '../../auth/state';
import cacheManager from '../../auth/cache_manager';
import './faqs.css';

function FAQs() {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { user, isAuthenticated } = useAuthStore();  // # Get user and auth state

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Add hero background style
  const heroStyle = {
    background: `linear-gradient(rgba(44, 62, 80, 0.7), rgba(44, 62, 80, 0.7)), url(${process.env.PUBLIC_URL + '/icons/landing/faqs_neo.jpg'})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  };

  // # State to track which FAQ is open
  const [openFAQ, setOpenFAQ] = useState(null);

  // # FAQ data
  const faqData = [
    {
      question: "Are mentorship sessions one-on-one or group-based?",
      answer: [
        "Mentorship sessions are primarily one-on-one to ensure personalized attention.",
        "Group sessions may be held for specific topics every now and then."
      ]
    },
    {
      question: "Will I have one mentor or multiple mentors?",
      answer: [
        "You will primarily work with one dedicated mentor who will handle your meetings, assignments, follow-ups, and Q&A. This ensures your mentor is familiar with your data, strengths, weaknesses, and unique needs.",
        "However, it's not just one mentor's expertise you're getting. Our entire team of mentors has contributed to crafting the program, so you benefit from all their collective experience."
      ]
    },
    {
      question: "Will I need any books with the library?",
      answer: [
        "No! You will NEVER need any other book with our library",
        "Our library is comprehensive, yet concise! Supplemented with the best photos, videos and illustrations that will make you comfortable dealing with any topic",
        <Link key="samples" to="/samples">Check out our amazing Library samples</Link>,
        <Link key="library" to="/products">Check out our pricing page</Link>
      ]
    },
    {
      question: "What if I want to quit mentorship and continue with the library alone?",
      answer: [
        "If you had been mentored for at least 3 months, you will get a 30% discount on (library only) subscription.",
        "If you had been mentored for at least 6 months, you will get a 100% discount on (library only) subscription for 3 months, then 30% thereafter."
      ]
    },
    {
      question: "How can I get the group application discount?",
      answer: [
        "One of the group should reach out to us to get a discount coupon",
        "Each member will fill the application with the discount coupon",
        "Once 5 members apply using the coupon, it will be activated"
      ]
    },
    {
      question: "If I subscribe to the mentorship program for step 1, will I have to pay the first month deposit again for step 2?",
      answer: [
        "No, the first month premium is only for the first month of all your steps preparation + there is a free 3-month break allowance between Step 1 exam and Step 2 preparation with no monthly fees"
      ]
    }
  ];

  // # Toggle FAQ
  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <div className={`fqs_faqs-page ${isDarkMode ? 'fqs_dark-mode' : ''}`}>
      {isAuthenticated ? (
        <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      ) : (
        <Header />
      )}
      
      <main className="fqs_faqs-content">
        <section className="fqs_hero-section" style={heroStyle}>
          <h1>Frequently Asked Questions</h1>
          <p>Find answers to common questions about TEMP-U's services</p>
        </section>

        <section className="fqs_faq-section">
          <div className="fqs_faq-container">
            {faqData.map((faq, index) => (
              <div 
                key={index}
                className={`fqs_faq-item ${openFAQ === index ? 'active' : ''}`}
                onClick={() => toggleFAQ(index)}
              >
                <div className="fqs_faq-question">
                  <h3>{faq.question}</h3>
                  <span className="fqs_faq-icon">
                    {openFAQ === index ? '−' : '+'}
                  </span>
                </div>
                <div className="fqs_faq-answer">
                  {faq.answer.map((paragraph, pIndex) => (
                    <p key={pIndex}>{paragraph}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default FAQs;
